import React, { useEffect, useMemo, useRef } from 'react';
import { Marker, Polyline, useMap } from 'react-leaflet';
import { Divider } from 'antd';
import L from 'leaflet';

import { LeafletMap } from './LeafletMap';
import { DEFAUL_MARKER_ICON, MARKER_PLACE_ICON_KDETOSAKRA } from '../../constants/icons';
import { resultPathColors } from '../../constants/game';
import useSMapResize from '../../hooks/useSMapResize';

// Custom ikony
const guessMarkerIcon = L.icon({
    iconUrl: DEFAUL_MARKER_ICON,
    iconSize: [22, 31],
    iconAnchor: [11, 31],
});

const panoramaMarkerIcon = L.icon({
    iconUrl: MARKER_PLACE_ICON_KDETOSAKRA,
    iconSize: [31, 31],
    iconAnchor: [15, 31],
});

interface GuessedPoint {
    pointMap: { x: number; y: number };
    pointPanorama: { x: number; y: number };
}

interface ResultMapProps {
    /** Pole všech odhadů */
    guessedPoints: (GuessedPoint | null)[];
    /** CSS třída */
    className?: string;
}

/**
 * Komponenta pro automatické přizpůsobení pohledu na všechny body
 */
const FitBoundsHandler: React.FC<{ guessedPoints: (GuessedPoint | null)[] }> = ({
    guessedPoints,
}) => {
    const map = useMap();
    const hasFittedRef = useRef(false);

    useEffect(() => {
        if (hasFittedRef.current) return;

        const validPoints = guessedPoints.filter((p): p is GuessedPoint => p !== null);
        if (validPoints.length === 0) return;

        const allCoords: [number, number][] = [];
        validPoints.forEach((point) => {
            // Pozor: Leaflet používá [lat, lon], data mají x=lon, y=lat
            allCoords.push([point.pointPanorama.y, point.pointPanorama.x]);
            allCoords.push([point.pointMap.y, point.pointMap.x]);
        });

        if (allCoords.length > 0) {
            const bounds = L.latLngBounds(allCoords);
            map.fitBounds(bounds, {
                padding: [30, 30],
                maxZoom: 15,
                animate: true,
            });
            hasFittedRef.current = true;
        }
    }, [guessedPoints, map]);

    return null;
};

/**
 * Vykreslení výsledku jednoho kola
 */
const RoundResult: React.FC<{
    point: GuessedPoint;
    index: number;
}> = ({ point, index }) => {
    const color = resultPathColors[index] || '#f00';

    // Pozor: Leaflet používá [lat, lon], data mají x=lon, y=lat
    const guessPosition: [number, number] = [point.pointMap.y, point.pointMap.x];
    const panoramaPosition: [number, number] = [point.pointPanorama.y, point.pointPanorama.x];

    return (
        <>
            <Marker
                position={guessPosition}
                icon={guessMarkerIcon}
            />
            <Marker
                position={panoramaPosition}
                icon={panoramaMarkerIcon}
            />
            <Polyline
                positions={[guessPosition, panoramaPosition]}
                color={color}
                weight={3}
            />
        </>
    );
};

/**
 * Mapa pro zobrazení výsledků hry
 *
 * Zobrazuje:
 * - Všechny odhady hráče (červené markery)
 * - Všechny správné pozice panoramat (zelené markery)
 * - Čáry spojující odhad se správnou pozicí (různé barvy pro kola)
 * - Automaticky přizpůsobí pohled na všechny body
 *
 * @example
 * <ResultMap guessedPoints={game.guessedPoints} />
 */
export const ResultMap: React.FC<ResultMapProps> = ({ guessedPoints, className = '' }) => {
    const { width } = useSMapResize();

    // Výpočet počátečního středu z prvního bodu
    const initialCenter = useMemo((): [number, number] => {
        const firstPoint = guessedPoints.find((p) => p !== null);
        if (firstPoint) {
            return [firstPoint.pointPanorama.y, firstPoint.pointPanorama.x];
        }
        return [50.027429, 15.202828]; // Střed ČR
    }, [guessedPoints]);

    return (
        <div id="smap-container">
            {width <= 961 ? <Divider /> : null}
            <div id="smap" className={`smap-result smap-style ${className}`}>
                <LeafletMap
                    center={initialCenter}
                    zoom={7}
                    showLayerControl={false}
                >
                    <FitBoundsHandler guessedPoints={guessedPoints} />

                    {guessedPoints.map((point, index) =>
                        point ? (
                            <RoundResult
                                key={`result-${index}`}
                                point={point}
                                index={index}
                            />
                        ) : null
                    )}
                </LeafletMap>
            </div>
        </div>
    );
};

export default ResultMap;
